import type { Compania } from '../services/companiaService';
import type { InformeIvaSimple, IvaSimpleCategoria, IvaSimpleSeccion } from './ivaSimple';
import { calcularDiferenciaIvaSimple } from './ivaSimple';
import { formatCurrency } from './formatters';
import { lineasDatosCompania } from './pdfLibroIvaCompania';

type FilaPdfKind = 'titulo' | 'subtitulo' | 'fila' | 'exento' | 'total' | 'diferencia';

interface FilaPdfIvaSimple {
  cells: string[];
  kind: FilaPdfKind;
}

const formatearFechaPdf = (fecha: Date | string): string => {
  const date = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(date.getTime())) return '';
  const dia = date.getDate().toString().padStart(2, '0');
  const mes = (date.getMonth() + 1).toString().padStart(2, '0');
  const año = date.getFullYear();
  return `${dia}/${mes}/${año}`;
};

const formatearEmisionPdf = (fecha: Date): string => fecha.toLocaleString('es-AR');

const formatearAlicuotaPdf = (alicuota: number): string => `${alicuota.toFixed(2)} %`;

function parseInputDateYmd(value: string): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function appendCategoria(filas: FilaPdfIvaSimple[], cat: IvaSimpleCategoria) {
  if (cat.lineas.length === 0 && cat.montoExento == null) {
    filas.push({ cells: [cat.etiqueta, '', '', '', ''], kind: 'fila' });
    return;
  }

  if (!cat.lineas.length && cat.montoExento != null && cat.montoExento !== 0) {
    filas.push({
      cells: [cat.etiqueta, '', '', '', formatCurrency(cat.montoExento)],
      kind: 'exento',
    });
    return;
  }

  cat.lineas.forEach((linea, idx) => {
    filas.push({
      cells: [
        idx === 0 ? cat.etiqueta : '',
        formatearAlicuotaPdf(linea.alicuota),
        formatCurrency(linea.neto),
        formatCurrency(linea.iva),
        formatCurrency(linea.total),
      ],
      kind: 'fila',
    });
  });
}

function appendSeccion(filas: FilaPdfIvaSimple[], seccion: IvaSimpleSeccion) {
  filas.push({ cells: [seccion.titulo, '', '', '', ''], kind: 'titulo' });
  filas.push({ cells: [seccion.subtitulo, '', '', '', ''], kind: 'subtitulo' });
  seccion.categorias.forEach((cat) => appendCategoria(filas, cat));
  filas.push({
    cells: [
      `Total ${seccion.subtitulo}`,
      '',
      formatCurrency(seccion.total.neto),
      formatCurrency(seccion.total.iva),
      formatCurrency(seccion.total.total),
    ],
    kind: 'total',
  });
}

function filasPdfDesdeInforme(informe: InformeIvaSimple): FilaPdfIvaSimple[] {
  const filas: FilaPdfIvaSimple[] = [];
  appendSeccion(filas, informe.ventas);
  appendSeccion(filas, informe.compras);
  const diferencia = calcularDiferenciaIvaSimple(informe);
  filas.push({
    cells: [
      'Diferencia (Débito-Crédito)',
      '',
      formatCurrency(diferencia.neto),
      formatCurrency(diferencia.iva),
      formatCurrency(diferencia.total),
    ],
    kind: 'diferencia',
  });
  return filas;
}

export async function generarPdfIvaSimple(params: {
  informe: InformeIvaSimple;
  compania: Compania | null;
  fechaDesde: string;
  fechaHasta: string;
  emitidoEn?: Date;
  logoPng?: string | null;
}): Promise<Blob> {
  const { informe, compania, fechaDesde, fechaHasta, emitidoEn = new Date(), logoPng } = params;
  const filas = filasPdfDesdeInforme(informe);

  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const marginX = 40;
  const pageW = doc.internal.pageSize.getWidth();
  const headerTop = 32;
  const logoMaxW = 76;
  const logoMaxH = 42;
  const rightColW = 228;
  const rightX = pageW - marginX;

  if (logoPng) {
    doc.addImage(logoPng, 'PNG', marginX, headerTop, logoMaxW, logoMaxH, undefined, 'FAST');
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(25, 55, 95);
  doc.text('IVA Simple', pageW / 2, headerTop + 26, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);

  doc.setFontSize(7);
  const datosComp = compania ? lineasDatosCompania(compania) : [];
  let ry = headerTop + 4;
  datosComp.forEach((linea) => {
    const wrapped = doc.splitTextToSize(linea, rightColW);
    wrapped.forEach((sub: string) => {
      doc.text(sub, rightX, ry, { align: 'right' });
      ry += 8;
    });
  });

  const headerBottom = Math.max(headerTop + logoMaxH + 4, ry + 4, headerTop + 48);
  let currentY = headerBottom;

  doc.setFontSize(8);
  doc.text(`Generado: ${formatearEmisionPdf(emitidoEn)}`, marginX, currentY);
  currentY += 12;

  const desde = parseInputDateYmd(fechaDesde);
  const hasta = parseInputDateYmd(fechaHasta);
  doc.text(
    `Período: ${desde ? formatearFechaPdf(desde) : '—'} - ${hasta ? formatearFechaPdf(hasta) : '—'}`,
    marginX,
    currentY
  );
  currentY += 14;

  const tableWidth = pageW - marginX * 2;
  const autoTable = (await import('jspdf-autotable')).default;

  autoTable(doc, {
    startY: currentY,
    margin: { left: marginX, right: marginX },
    tableWidth,
    body: filas.map((f) => f.cells),
    theme: 'plain',
    styles: {
      fontSize: 7.5,
      cellPadding: { top: 1.5, right: 2, bottom: 1.5, left: 2 },
      overflow: 'linebreak',
      valign: 'middle',
      lineWidth: 0,
      lineColor: [255, 255, 255],
    },
    columnStyles: {
      0: { cellWidth: tableWidth * 0.42 },
      1: { cellWidth: tableWidth * 0.11, halign: 'right' },
      2: { cellWidth: tableWidth * 0.15, halign: 'right' },
      3: { cellWidth: tableWidth * 0.15, halign: 'right' },
      4: { cellWidth: tableWidth * 0.17, halign: 'right' },
    },
    didParseCell: (data) => {
      data.cell.styles.lineWidth = 0;

      if (data.section !== 'body') return;
      const fila = filas[data.row.index];
      if (!fila) return;

      if (fila.kind === 'titulo') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = 10;
        data.cell.styles.cellPadding = { top: 6, right: 2, bottom: 2, left: 2 };
      } else if (fila.kind === 'subtitulo') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = 8;
      } else if (fila.kind === 'total') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [240, 244, 248];
      } else if (fila.kind === 'diferencia') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [227, 242, 253];
        data.cell.styles.cellPadding = { top: 4, right: 2, bottom: 4, left: 2 };
      }

      if (data.column.index > 0) {
        data.cell.styles.halign = 'right';
      }
    },
  });

  const pdfBlob = doc.output('blob');
  return pdfBlob instanceof Blob && pdfBlob.type === 'application/pdf'
    ? pdfBlob
    : new Blob([pdfBlob], { type: 'application/pdf' });
}
